import { useEffect, useState } from "react";
import { Gauge, Loader2, Check, ShieldAlert } from "lucide-react";
import client from "../lib/api";

/**
 * Super-admin: Klair interaction quota.
 *
 * Sets the per-day grant every person receives and the ceiling their
 * unused allowance can bank up to. Platform admin only; anyone else
 * sees a friendly refusal. Demo personas are always exempt from the
 * quota regardless of these numbers.
 */
export default function AdminKlairQuotaScreen() {
  const [allowed, setAllowed] = useState(null);
  const [perDay, setPerDay] = useState(2);
  const [ceiling, setCeiling] = useState(14);
  const [busy, setBusy] = useState(false);
  const [ok, setOk] = useState("");
  const [err, setErr] = useState("");

  useEffect(() => {
    client.get("/klair/quota/config")
      .then((r) => { setAllowed(true); setPerDay(r.data.per_day); setCeiling(r.data.ceiling); })
      .catch((e) => setAllowed(e?.response?.status === 403 ? false : false));
  }, []);

  const save = async () => {
    setBusy(true); setOk(""); setErr("");
    try {
      const { data } = await client.put("/klair/quota/config", { per_day: Number(perDay), ceiling: Number(ceiling) });
      setPerDay(data.per_day); setCeiling(data.ceiling);
      setOk("Saved. New sign-ins and grants use these numbers.");
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not save. Check the numbers and try again.");
    } finally { setBusy(false); }
  };

  if (allowed === null) {
    return <div className="max-w-3xl mx-auto px-5 pt-16 text-center"><Loader2 size={22} className="animate-spin mx-auto" style={{ color: "hsl(var(--klareet-muted))" }} /></div>;
  }
  if (!allowed) {
    return (
      <div className="max-w-2xl mx-auto px-5 pt-16 text-center" data-testid="klair-quota-refused">
        <div className="w-14 h-14 rounded-full mx-auto mb-4 flex items-center justify-center" style={{ background: "hsl(var(--destructive) / 0.12)", color: "hsl(var(--destructive))" }}>
          <ShieldAlert size={26} />
        </div>
        <h1 className="klareet-serif mb-2" style={{ fontSize: "1.6rem" }}>This is a super-admin area.</h1>
        <p className="k-quiet">You need the platform administrator role to set the Klair quota.</p>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-5 pt-8 pb-24">
      <div className="k-label mb-2 flex items-center gap-2"><Gauge size={14} /> super admin</div>
      <h1 className="klareet-serif mb-2" style={{ fontSize: "clamp(1.8rem, 4vw, 2.4rem)", lineHeight: 1.1 }} data-testid="klair-quota-title">
        Klair interaction quota.
      </h1>
      <p className="k-hero-sub mb-6">How many Klair interactions each person gets a day, and how many they can bank. Demo accounts are always exempt.</p>

      <div className="k-card space-y-5" data-testid="klair-quota-form">
        <div>
          <label className="k-label mb-1 block">interactions per day</label>
          <input type="number" min={0} max={100} className="k-input klareet-mono" style={{ maxWidth: "10rem" }}
            value={perDay} onChange={(e) => setPerDay(e.target.value)} data-testid="quota-per-day" />
          <p className="k-quiet !text-xs mt-1">Each person is granted this many fresh interactions every day.</p>
        </div>
        <div>
          <label className="k-label mb-1 block">carry-over ceiling</label>
          <input type="number" min={0} max={1000} className="k-input klareet-mono" style={{ maxWidth: "10rem" }}
            value={ceiling} onChange={(e) => setCeiling(e.target.value)} data-testid="quota-ceiling" />
          <p className="k-quiet !text-xs mt-1">Unused interactions bank up to this many. Must be at least the daily grant.</p>
        </div>
        {err && <div className="text-sm" style={{ color: "hsl(var(--destructive))" }} data-testid="quota-error">{err}</div>}
        {ok && <div className="text-sm flex items-center gap-1.5" style={{ color: "hsl(var(--klareet-teal))" }} data-testid="quota-saved"><Check size={14} /> {ok}</div>}
        <div>
          <button onClick={save} disabled={busy} className="k-btn k-btn-primary" data-testid="quota-save">
            {busy ? <Loader2 size={15} className="animate-spin" /> : <Gauge size={14} />}
            {busy ? "Saving" : "Save quota"}
          </button>
        </div>
      </div>
    </div>
  );
}
